// src/App.jsx
import './App.css';
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from "react-router-dom";
import { RangeProvider } from "./context/RangeContext/RangeContext";
import { GameProvider } from "./context/GameContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LoginScreen } from "./screens/LoginScreen";
import { TableView } from "./screens/TableView";
import { QuizConfigScreen } from "./screens/QuizConfigScreen";
import { RangeScreen } from "./screens/RangeScreen";
import { PreflopView } from "./screens/PreflopView";
import AnswerScreen from "./screens/AnswerScreen/AnswerScreen";
import ResultsScreen from "./screens/ResultsScreen/ResultsScreen";
import { ViewRangeScreen } from "./screens/ViewRangeScreen/ViewRangeScreen";
import DashboardScreen from "./screens/DashboardScreen";
import { AccountScreen } from "./screens/AccountScreen";
// BLAST Tournament screens
import BlastConfigScreen from "./screens/BlastConfigScreen/BlastConfigScreen";
import BlastRangeScreen from "./screens/BlastRangeScreen/BlastRangeScreen";
import { BlastView } from "./screens/BlastView";
import BlastAnswerScreen from "./screens/BlastAnswerScreen/BlastAnswerScreen";
import BlastResultsScreen from "./screens/BlastResultsScreen/BlastResultsScreen";
import { BlastViewRangeScreen } from "./screens/BlastViewRangeScreen/BlastViewRangeScreen";
// Unlisted animation prototype — not linked from any nav, see /blast-test route below.
import BlastAnimationTest from "./screens/BlastAnimationTest/BlastAnimationTest";
import { initializeClientDatabase, checkDatabaseConnection } from "./utils/databaseUtils";
import ScaleWrapper from "./components/layout/ScaleWrapper/ScaleWrapper";
import { AppLoadingScreen } from "./components/common/AppLoadingScreen/AppLoadingScreen";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";

// Status text shown under the AppLoadingScreen graphic while the app is
// still booting (initStatus.isInitializing below), keyed by
// initStatus.mode (see the setInitStatus calls inside initializeApp).
// Same shell also renders in ProtectedRoute (auth check) and on
// LoginScreen (sign-in / post-auth) — see AppLoadingScreen — so this text
// is the only thing that changes as the app boots.
const BOOT_STATUS_TEXT = {
  starting: 'Starting up…',
  localQuizData: 'Setting things up…',
  database: 'Connecting to database…',
  localStorage: 'Getting things ready…',
};

import './import-jsx-runtime.js';


// Initialize Quiz Data - now with async support
const initializeQuizData = async () => {
  try {
    console.log('Initializing quiz data...');
    
    // Make sure localStorage is available
    if (typeof localStorage === 'undefined') {
      console.error('localStorage is not available');
      return false;
    }
    
    // Check if quiz_results exists
    const existingData = localStorage.getItem('quiz_results');
    
    if (!existingData) {
      console.log('Creating new quiz_results in localStorage');
      
      // Empty array of quiz results
      const emptyResults = [];
      
      // Store as a valid JSON array
      localStorage.setItem('quiz_results', JSON.stringify(emptyResults));
      console.log('Initialized empty quiz_results array');
    } else {
      console.log('quiz_results already exists in localStorage');
      
      // Try to validate stored data
      try {
        const parsedData = JSON.parse(existingData);
        
        // If it's not an array, reset it
        if (!Array.isArray(parsedData)) {
          console.warn('quiz_results is not an array, resetting');
          localStorage.setItem('quiz_results', JSON.stringify([]));
        } else {
          console.log(`quiz_results contains ${parsedData.length} quiz entries`);
        }
      } catch (parseError) {
        console.error('Error parsing quiz_results, resetting:', parseError);
        localStorage.setItem('quiz_results', JSON.stringify([]));
      }
    }
    
    return true;
  } catch (error) {
    console.error('Failed to initialize quiz data:', error);
    return false;
  }
};

// Redirects unauthenticated users to /login
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  // AuthContext is still resolving (Firebase auth state on cold launch can
  // take a moment, especially over a slow connection) — this used to
  // render nothing at all, indistinguishable from the app having silently
  // broken. Same boot shell as App.jsx's own init gate below (graphic +
  // title + status text), so there's no visual seam between "app is
  // initializing" and "app is checking whether you're signed in" — it
  // reads as one continuous loading screen, not a jump to a bare spinner.
  if (loading) return <AppLoadingScreen status="Signing you in…" />;
  return user ? children : <Navigate to="/login" replace />;
};

const AppRoutes = () => {
  console.log('AppRoutes rendering');
  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      <Route path="/dashboard"    element={<ProtectedRoute><DashboardScreen /></ProtectedRoute>} />
      <Route path="/account"      element={<ProtectedRoute><AccountScreen /></ProtectedRoute>} />
      <Route path="/quiz-config"  element={<ProtectedRoute><QuizConfigScreen /></ProtectedRoute>} />
      <Route path="/range"        element={<ProtectedRoute><RangeScreen /></ProtectedRoute>} />
      <Route path="/cash"         element={<ProtectedRoute><TableView type="cash" /></ProtectedRoute>} />
      <Route path="/preflop"      element={<ProtectedRoute><PreflopView /></ProtectedRoute>} />
      <Route path="/answer"       element={<ProtectedRoute><AnswerScreen /></ProtectedRoute>} />
      <Route path="/results"      element={<ProtectedRoute><ResultsScreen /></ProtectedRoute>} />
      <Route path="/view-range"   element={<ProtectedRoute><ViewRangeScreen /></ProtectedRoute>} />
      {/* BLAST Tournament routes */}
      <Route path="/blast-config"    element={<ProtectedRoute><BlastConfigScreen /></ProtectedRoute>} />
      <Route path="/blast-range"     element={<ProtectedRoute><BlastRangeScreen /></ProtectedRoute>} />
      <Route path="/blast"           element={<ProtectedRoute><BlastView /></ProtectedRoute>} />
      <Route path="/blast-answer"    element={<ProtectedRoute><BlastAnswerScreen /></ProtectedRoute>} />
      <Route path="/blast-view-range" element={<ProtectedRoute><BlastViewRangeScreen /></ProtectedRoute>} />
      <Route path="/blast-results"   element={<ProtectedRoute><BlastResultsScreen /></ProtectedRoute>} />
      {/* Unlisted prototype — hero-acts/villain-reacts reveal animation, mock
          data only. Not linked from any nav; reachable only by typing the URL. */}
      <Route path="/blast-test"      element={<ProtectedRoute><BlastAnimationTest /></ProtectedRoute>} />
    </Routes>
  );
};

function App() {
  const [initStatus, setInitStatus] = useState({
    isInitializing: true,
    error: null,
    mode: 'starting'
  });

  // Native-only, one-time status bar setup. Capacitor.isNativePlatform() is
  // false in a plain browser tab or a home-screen PWA — this is a no-op
  // there, so it's safe to leave in during the PWA-to-native transition.
  // setOverlaysWebView(true) makes the status bar transparent so our own
  // page content shows through it (same intent as the old
  // apple-mobile-web-app-status-bar-style: black-translucent meta tag, but
  // consistent every time instead of only applying in standalone mode).
  // Per-screen icon color (light/dark) is handled by
  // useScaleWrapperBackground — this just sets the one-time overlay mode
  // and a sane default before any screen-specific hook runs.
  // Hides the native launch screen (Splash.imageset — now the Loading.svg
  // graphic) once this component has actually mounted and painted, instead
  // of Capacitor auto-hiding it the instant the WKWebView is attached. With
  // launchAutoHide:false (see capacitor.config.json), the native splash
  // covers the entire gap between "WKWebView exists" and "first real
  // content is on screen" — which used to show through as a black flash,
  // since capacitor.config.json's server.url loads rangedojo.com live over
  // the network rather than the bundled dist/ files, so there was a real
  // network+JS boot gap with nothing covering it.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    SplashScreen.hide().catch(() => {});
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
    // Style.Light = dark/black icons, correct default against the app's
    // light cream background (SCALE_WRAPPER_DEFAULT_BG) used on nearly
    // every screen. Previously Style.Dark (white icons), which is only
    // right for dark backgrounds — DashboardScreen's empty state is the
    // one screen dark enough to need that, and it opts into it itself via
    // useScaleWrapperBackground.
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  }, []);

  useEffect(() => {
    // Set global flag for tracking initialization
    window.appInitialized = false;
    window.usingLocalStorageOnly = false; // Start by assuming we'll use the database
    
    const initializeApp = async () => {
      try {
        console.log('Starting app initialization...');
        setInitStatus({ isInitializing: true, error: null, mode: 'starting' });
        
        // Initialize quiz data (local storage)
        setInitStatus({ isInitializing: true, error: null, mode: 'localQuizData' });
        await initializeQuizData();
        
        // Initialize database with proper error handling
        setInitStatus({ isInitializing: true, error: null, mode: 'database' });
        try {
          const dbResult = await initializeClientDatabase();
          console.log('Database initialization completed with status:', dbResult);
          setInitStatus({ 
            isInitializing: true, 
            error: null, 
            mode: window.usingLocalStorageOnly ? 'localStorage' : 'database' 
          });
        } catch (dbError) {
          console.error('Database initialization error:', dbError);
          window.usingLocalStorageOnly = true;
          setInitStatus({ 
            isInitializing: true, 
            error: dbError, 
            mode: 'localStorage' 
          });
        }
    
        console.log('Starting database connection check...');
        try {
          const connectionStatus = await Promise.race([
            checkDatabaseConnection(),
            new Promise((_, reject) => 
              setTimeout(() => {
                console.warn('Database connection timeout exceeded');
                reject(new Error('Connection timeout'));
              }, 10000)
            )
          ]);

          console.log('Connection status details:', connectionStatus);
          
          // CRITICAL: Only use database mode if connection was SUCCESSFUL
          // The simple health check passing isn't enough
          if (!connectionStatus || !connectionStatus.success) {
            console.warn('Database unavailable, switching to localStorage mode');
            window.usingLocalStorageOnly = true;
          } else {
            console.log('Database confirmed available, using database mode');
            window.usingLocalStorageOnly = false;
          }
        } catch (connectionError) {
          console.error('Database connection error:', connectionError);
          window.usingLocalStorageOnly = true;
        }
                
        // Mark initialization as complete
        window.appInitialized = true;
        console.log('App initialization complete - using mode:', window.usingLocalStorageOnly ? 'localStorage' : 'database');
        setInitStatus({ 
          isInitializing: false, 
          error: null, 
          mode: window.usingLocalStorageOnly ? 'localStorage' : 'database' 
        });
      } catch (err) {
        console.error('App initialization error:', err);
        // Set a flag to indicate we're in fallback mode
        window.usingLocalStorageOnly = true;
        setInitStatus({ 
          isInitializing: false, 
          error: err, 
          mode: 'localStorage' 
        });
      }
    };
    
    // Start initialization process
    initializeApp();
    
    // Prevent body bounce/overscroll, but allow scrolling inside any element
    // that actually has overflow content.
    // NOTE: this used to only check #app's own scrollHeight, back when #app
    // itself was the thing that scrolled. Screens like BlastConfigScreen /
    // QuizConfigScreen now scroll via their own internal container
    // (.quiz-config-scroll) instead, so #app never overflows anymore and the
    // old check made this fire preventDefault() on every touchmove in the
    // app — silently killing touch-scroll everywhere (invisible on desktop,
    // since wheel scroll isn't a touchmove event, but total on real phones).
    // Walk up from the touch target to find the nearest scrollable ancestor
    // and defer to native scrolling there instead.
    const preventDefault = (e) => {
      let node = e.target;
      while (node && node !== document.body) {
        if (node.scrollHeight > node.clientHeight) {
          const { overflowY } = window.getComputedStyle(node);
          if (overflowY === 'auto' || overflowY === 'scroll') return;
        }
        node = node.parentElement;
      }
      e.preventDefault();
    };
    document.body.addEventListener('touchmove', preventDefault, { passive: false });
    
    return () => {
      document.body.removeEventListener('touchmove', preventDefault);
    };
  }, []);

  // Single ScaleWrapper for both the boot gate and the real app, so the
  // AppLoadingScreen graphic is laid out at the exact same scale as
  // LoginScreen's — previously this early return bypassed ScaleWrapper
  // entirely, which made the boot screen a different effective size than
  // every other screen on any device that isn't exactly 375x812.
  return (
    <ScaleWrapper>
      {initStatus.isInitializing ? (
        <AppLoadingScreen status={BOOT_STATUS_TEXT[initStatus.mode] || BOOT_STATUS_TEXT.starting} />
      ) : (
        <AuthProvider>
          <GameProvider>
            <RangeProvider>
              <AppRoutes />
            </RangeProvider>
          </GameProvider>
        </AuthProvider>
      )}
    </ScaleWrapper>
  );
}

export default App;